import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles, Home, ArrowRight, Check,
  Building2, ChevronRight, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_MAIN = 'https://customer-assets.emergentagent.com/job_09ef4963-c96f-46c6-a08d-052afb978ee9/artifacts/j66rn420_Gleam%20Guru%20Cleaning%20%5BLogo%20Design%5D%20No%20Gleam.png';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.12 } },
};

const PHONE = '(971) 715-0335';

const SERVICES = [
  {
    title: 'Standard Home Clean',
    serviceKey: 'standard_home',
    tag: 'Residential',
    desc: 'Regular maintenance cleaning to keep your home fresh, healthy, and welcoming. Perfect for weekly, biweekly, or monthly schedules.',
    benefits: [
      'All rooms cleaned and tidied',
      'Kitchen and bath surfaces sanitized',
      'Floors vacuumed and mopped throughout',
      'Dusting of accessible surfaces',
      'Beds made and trash emptied',
    ],
    time: '2-3 hours',
    icon: Home,
    img: 'https://images.unsplash.com/photo-1704040686413-2c607dbd2f06?w=600&q=80',
  },
  {
    title: 'Deep Reset Clean',
    serviceKey: 'deep_reset',
    tag: 'Residential',
    desc: 'A comprehensive top-to-bottom transformation. Every surface, corner, and fixture receives thorough attention.',
    benefits: [
      'Complete kitchen deep clean including appliances',
      'All bathrooms sanitized and detailed',
      'Dusting of vents, fixtures, and high surfaces',
      'Baseboards, door frames, and switch plates',
      'Inside cabinets and window tracks',
    ],
    time: '4-6 hours',
    icon: Sparkles,
    img: 'https://images.unsplash.com/photo-1602028915047-37269d1a73f7?w=600&q=80',
  },
  {
    title: 'Airbnb Turnover',
    serviceKey: 'airbnb',
    tag: 'Residential',
    desc: 'Fast, reliable turnover cleaning that keeps your reviews high and guests happy. Same-day availability.',
    benefits: [
      'Full property clean between guests',
      'Linens changed and beds made',
      'Kitchen and bathrooms deep cleaned',
      'Restocking of essentials',
      'Photo-ready presentation',
    ],
    time: '2-4 hours',
    icon: Clock,
    img: 'https://images.unsplash.com/photo-1711059985570-4c32ed12a12c?w=600&q=80',
  },
  {
    title: 'Commercial / Retail Clean',
    serviceKey: 'commercial',
    tag: 'Commercial',
    desc: 'Professional recurring cleaning for offices, retail spaces, storefronts, and commercial environments.',
    benefits: [
      'Custom cleaning schedule to fit your hours',
      'Reception and common areas maintained',
      'Restrooms sanitized and restocked',
      'Floor care and surface disinfection',
      'Break rooms and kitchenettes cleaned',
    ],
    time: 'Custom',
    icon: Building2,
    img: 'https://images.unsplash.com/photo-1718220216044-006f43e3a9b1?w=600&q=80',
  },
];

const ServicesPage = () => {
  return (
    <div style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Hero — Dark/Orange */}
      <section
        className="relative bg-zinc-950 overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28"
        data-testid="services-hero"
      >
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle, #F56F05 1px, transparent 1px)', backgroundSize: '28px 28px' }}
        />
        <motion.div
          className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-[#F56F05] opacity-[0.13] blur-[140px] pointer-events-none"
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-xs uppercase tracking-[0.3em] font-bold text-[#F56F05] mb-5"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Our Services
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white mb-6 tracking-tight leading-[0.95]"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Premium Cleaning<br />
            <span className="bg-gradient-to-br from-[#FFB85C] via-[#F56F05] to-[#C2410C] bg-clip-text text-transparent">For Every Space.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-zinc-400 max-w-2xl mx-auto"
          >
            From routine home maintenance to commercial contracts, we deliver consistent, detail-driven results built around your schedule.
          </motion.p>
        </div>
      </section>

      {/* Service Cards */}
      <section className="bg-white py-16 lg:py-24" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {SERVICES.map((svc, idx) => (
            <motion.article
              key={svc.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ delay: (idx % 2) * 0.1 }}
              whileHover={{ y: -4 }}
              className="group flex flex-col bg-white rounded-3xl overflow-hidden border-2 border-zinc-100 hover:border-[#F56F05]/40 hover:shadow-xl hover:shadow-orange-50 transition-all"
              data-testid={`service-card-${idx}`}
            >
              {/* Image */}
              <div className="relative aspect-[16/10] overflow-hidden">
                <img src={svc.img} alt={svc.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/40 to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="bg-zinc-950/80 backdrop-blur-sm text-xs font-bold text-white px-3 py-1.5 rounded-full border border-white/10">{svc.tag}</span>
                </div>
                <div className="absolute bottom-4 right-4 bg-zinc-950/80 backdrop-blur-sm text-xs font-bold text-white px-3 py-1.5 rounded-full flex items-center gap-1 border border-white/10">
                  <Clock className="w-3 h-3 text-[#F56F05]" /> {svc.time}
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 flex flex-col p-6 sm:p-7">
                <div className="w-12 h-12 rounded-2xl bg-[#F56F05] flex items-center justify-center mb-4 shadow-lg shadow-[#F56F05]/30">
                  <svc.icon className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl sm:text-2xl font-extrabold text-zinc-900 mb-2 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{svc.title}</h2>
                <p className="text-sm text-zinc-500 leading-relaxed mb-5">{svc.desc}</p>
                <ul className="space-y-2.5 mb-6">
                  {svc.benefits.map(b => (
                    <li key={b} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-[#F56F05] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-zinc-700">{b}</span>
                    </li>
                  ))}
                </ul>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} className="mt-auto">
                  <Button
                    asChild
                    className="relative overflow-hidden bg-[#F56F05] hover:bg-[#C2410C] text-white px-6 py-5 text-sm rounded-full font-bold shadow-lg shadow-[#F56F05]/25 hover:shadow-xl hover:shadow-[#F56F05]/40 transition-all w-full sm:w-fit group/cta"
                  >
                    <Link to={`/?service=${svc.serviceKey}#quote-form`} data-testid={`service-cta-${idx}`}>
                      <span className="relative z-10 flex items-center">
                        Request This Service <ChevronRight className="ml-1 w-4 h-4 group-hover/cta:translate-x-1 transition-transform" />
                      </span>
                      <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover/cta:translate-x-full transition-transform duration-700 pointer-events-none" />
                    </Link>
                  </Button>
                </motion.div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* Bottom CTA — dark */}
      <section className="relative bg-zinc-950 py-20 lg:py-24 overflow-hidden border-t border-white/5" data-testid="services-bottom-cta">
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle, #F56F05 1px, transparent 1px)', backgroundSize: '28px 28px' }}
        />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-[#F56F05] opacity-[0.1] blur-[140px] pointer-events-none"
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <img src={LOGO_MAIN} alt="Gleam Guru" className="h-14 w-auto mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Gleam With The <span className="bg-gradient-to-br from-[#FFB85C] via-[#F56F05] to-[#C2410C] bg-clip-text text-transparent">Masters Of Clean</span>
          </h2>
          <p className="text-sm text-zinc-400 mb-8 max-w-lg mx-auto">
            Get in touch today and experience what premium cleaning feels like. No commitments, no pressure — just a conversation.
          </p>
          <div className="flex justify-center">
            <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.98 }}>
              <Button
                asChild
                className="relative overflow-hidden bg-[#F56F05] hover:bg-[#C2410C] text-white px-10 py-7 text-base rounded-full font-bold shadow-[0_20px_50px_-12px_rgba(245,111,5,0.55)] hover:shadow-[0_25px_60px_-12px_rgba(245,111,5,0.7)] transition-all group/cta"
              >
                <Link to="/?service=#quote-form">
                  <span className="relative z-10 flex items-center">
                    Let&apos;s Get Your Space Gleaming <ArrowRight className="ml-2 w-4 h-4 group-hover/cta:translate-x-1 transition-transform" />
                  </span>
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover/cta:translate-x-full transition-transform duration-700 pointer-events-none" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
